import Image from 'next/image';
import React, { useEffect, useState, useRef } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { getReferralCode } from 'aster-foundation/core/epharmacy/Referrals/redux/action';
import { dataLayerSendEvent } from 'Epharmacy/EpharmacyAnalytics';
import { ONE_ASTER_CATEGORY, ONLINE_PHARMACY_ROUTE } from '@common/Utility/constants';
import { flagsmith } from '@common/Lib/Flagsmith';
import { errorHandlingEpharmacy, getIsInstoreApp } from '@common/Utility/common-utils';
import { EventEmitter } from '@common/Utility/eventEmitter';
import { ButtonBase } from '@common/Component/Forms';
import { useRouter } from 'next/router';
import withAnalytics from '@common/Component/Hoc/WithAnalytics';
import { IC_COPY_IMAGE, REFERRAL_GIFT_BANNER, REFERRAL_GIFT_BANNER_COUPON } from '../../../../public/images';
import ReferralInfoBannerStyled from './ReferralInfoBannerStyled';
import { useNavigationURL } from '@common/Utility/generateNavigationURL';

const ReferralInfoBanner = ({ analyticsProps, position }) => {
  const generateURL = useNavigationURL();
  const referralFlag = flagsmith.getValue('ae_web_referral_coupon', { json: true });
  const commonAnalytics = {
    screen_name: analyticsProps?.screenName || 'order confirmation',
    page_type: analyticsProps?.pageType || 'checkout page',
    referrer_page: analyticsProps?.referrerPage || 'Epharmacy Checkout Page',
    myaster_category: ONE_ASTER_CATEGORY.PHARMACY,
  };
  const router = useRouter();
  const inViewRef = useRef();
  const bannerViewEventFired = useRef();
  const userLogin = useSelector((state) => state?.astercore?.checkUserExist?.data?.entry[0]?.resource);
  const [referralData, setReferralData] = useState();
  const [isClient, setIsClient] = useState(false);
  const instoreFlag = flagsmith.getValue('ae_web_instore_checkout', { json: true });

  const dispatch = useDispatch();

  const sendBannerImpressionEvent = (responseData) => {
    if (!responseData) return;
    dataLayerSendEvent('referral_banner_impression', {
      ...commonAnalytics,
      referral_code_generated: responseData?.referralCode ? 'true' : 'false',
      referral_code: responseData?.referralCode || '',
    });
  };

  useEffect(() => {
    setIsClient(true);
  }, []);

  const sendReferalCopiedEvent = () => {
    dataLayerSendEvent('referral_code_copied', {
      ...commonAnalytics,
      referral_code: referralData?.referralCode || '',
    });
  };

  useEffect(() => {
    if (!userLogin?.id || !referralFlag?.enabled) return;
    const params = { userId: userLogin?.id };

    dispatch(
      getReferralCode({
        params,
        onSuccess: (response) => {
          const data = response?.body;
          const referralResponse = {
            referralCode: data?.referralCode,
            referredAmount: data?.referredAmount,
            referrerAmount: data?.referrerAmount,
          };
          setReferralData(referralResponse);
          if (position === 'top') {
            bannerViewEventFired.current = true;
            sendBannerImpressionEvent(referralResponse);
          }
        },
        onFailure: (err) => {
          errorHandlingEpharmacy(err);
        },
      }),
    );
  }, [userLogin]);

  const copyToClipboard = () => {
    const el = document.createElement('textarea');
    el.value = referralData?.referralCode;
    document.body.appendChild(el);
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);
    sendReferalCopiedEvent();
    EventEmitter.dispatch('setToastControlMessage', { message: 'Referral Code has been copied' });
  };

  const sendBannerClickEvent = () => {
    dataLayerSendEvent('referral_banner_click', {
      ...commonAnalytics,
      referral_code_generated: referralData?.referralCode ? 'true' : 'false',
      referral_code: referralData?.referralCode || '',
    });
  };

  const navigateToReferralPage = () => {
    sendBannerClickEvent();
    router.push({ pathname: generateURL(`${ONLINE_PHARMACY_ROUTE}/referrals`) });
  };

  const handleOnView = () => {
    if (!bannerViewEventFired.current && referralData) {
      bannerViewEventFired.current = true;
      sendBannerImpressionEvent(referralData);
    }
  };

  const renderReferralBanner = () => {
    const isUserEligibleForReferral = userLogin?.extension?.some(
      (e) => e.url === 'referralEligibility' && e.valueBoolean,
    );

    if (!referralFlag?.enabled || !isUserEligibleForReferral || !referralData || !isClient || (getIsInstoreApp() && instoreFlag?.hidereferralBanner)) return null;
    return (
      <div className="referral-banner">
        <Container fluid className="p-0">
          <Row className="m-0">
            <Col className="p-0" xs={8} sm={8} md={10} lg={10}>
              {!referralData?.referralCode ? (
                <div className="title">Refer a Friend, Get Rewarded!</div>
              ) : (
                <div className="referral-coupon-code-container">
                  <div className="title-code">Referral code: </div>
                  <div onClick={copyToClipboard} className="coupon-box">
                    <div>{referralData?.referralCode}</div>
                    <div width="15px" height="15px">
                      <Image width="15px" height="15px" src={IC_COPY_IMAGE.src} />
                    </div>
                  </div>
                </div>
              )}
              {!referralData?.referralCode ? (
                <div className="description">Earn AED 30 for each referral and give AED 30 off to friends!</div>
              ) : (
                <div className="description-code">
                  Refer a friend and earn vouchers worth AED 30 on each successful referral.
                </div>
              )}
              <div className="refer-button">
                <ButtonBase
                  data-test="button4"
                  onClick={navigateToReferralPage}
                  size="sm"
                  variant="success"
                  text={referralData?.referralCode ? 'Refer Now' : 'Refer and Earn'}
                />
              </div>
            </Col>
            <Col>
              {!referralData ? (
                <Image width="116px" height="127px" src={REFERRAL_GIFT_BANNER.src} />
              ) : (
                <Image width="171" height="132" src={REFERRAL_GIFT_BANNER_COUPON.src} />
              )}
            </Col>
          </Row>
        </Container>
      </div>
    );
  };

  const ReferralBannerWithAnalytics = withAnalytics(
    () => (
      <ReferralInfoBannerStyled ref={inViewRef} hasCouponCode={referralData?.referralCode}>
        {renderReferralBanner()}
      </ReferralInfoBannerStyled>
    ),
    handleOnView,
    inViewRef,
  );

  return <ReferralBannerWithAnalytics />;
};

ReferralInfoBanner.propTypes = {
  analyticsProps: PropTypes.object.isRequired,
  position: PropTypes.oneOf(['top', 'middle']).isRequired,
};

export default ReferralInfoBanner;
